/**
 * Template rendering. The Queue screen's preview, the "send test" action, and
 * the cron Worker all call renderEmail(), so what you preview is byte-for-byte
 * what goes out.
 */

import { resolveFromName, resolveReplyTo, type SendCampaign, type SendIdentity } from './message'
import type { Lead, Template } from './types'

// --- the token registry ------------------------------------------------------
//
// Two classes, and the distinction is the whole point:
//
//   SENDER    same for every email in a send. Resolved server-side from the
//             active identity + campaign, NEVER from lead/CSV data. A missing
//             CSV column must not be able to blank a signature.
//   LEAD      per recipient, from the lead row and its CSV columns.
//
// Before this existed, `{{company}}` was resolved out of leads.fields — so the
// employer name was hand-copied into every CSV row (with inconsistent casing),
// and a CSV without that column rendered "The  Team" into real mail.

/** Constants for the whole send. Never sourced from a lead. */
export const SENDER_TOKENS = ['sender_name', 'company', 'sender_title', 'reply_to'] as const
/** Always available from the lead row itself. */
export const LEAD_TOKENS = ['first_name', 'name', 'email'] as const
/** Filled in by the send path, not by anyone's data. */
export const SYSTEM_TOKENS = ['unsubscribe_url'] as const

export type TokenKind = 'sender' | 'lead' | 'system' | 'custom'

export function classifyToken(token: string): TokenKind {
  if ((SENDER_TOKENS as readonly string[]).includes(token)) return 'sender'
  if ((LEAD_TOKENS as readonly string[]).includes(token)) return 'lead'
  if ((SYSTEM_TOKENS as readonly string[]).includes(token)) return 'system'
  return 'custom' // a CSV column, e.g. {{role}}
}

/** Where a user goes to fix an unresolved sender token. */
export const SENDER_TOKEN_SOURCE: Record<string, string> = {
  sender_name: 'set a From name on the campaign or the sending identity',
  company: 'set Company on the campaign (Details step)',
  sender_title: 'set a sender title on the campaign or the sending identity',
  reply_to: 'set Reply-to on the campaign or the sending identity',
}

/** The identity + campaign fields the sender tokens are resolved from. */
export interface SenderContext {
  identity: SendIdentity & { sender_title?: string | null }
  campaign?: (SendCampaign & { company?: string | null; sender_title?: string | null }) | null
}

/**
 * The four send-wide constants, resolved once.
 *
 * sender_name and reply_to reuse the SAME precedence helpers that decide the
 * real From / Reply-To headers, so what a template says can never disagree with
 * what the envelope says.
 */
export function resolveSenderVars(ctx: SenderContext): Record<string, string> {
  return {
    sender_name: resolveFromName(ctx.identity, ctx.campaign ?? undefined),
    company: (ctx.campaign?.company ?? '').trim(),
    sender_title: (ctx.campaign?.sender_title ?? ctx.identity.sender_title ?? '').trim(),
    reply_to: resolveReplyTo(ctx.identity, ctx.campaign ?? undefined) ?? '',
  }
}

/** Warn once per process per colliding column, rather than per email. */
const warnedCollisions = new Set<string>()

/**
 * `{{name}}`, `{{email}}`, every key in leads.fields, then the send-wide
 * constants — which deliberately come LAST so they win.
 *
 * A CSV column called `company` does not get to decide the employer name in the
 * signature; that is a property of the send, not of the recipient.
 */
export function mergeVars(
  lead: Pick<Lead, 'name' | 'email' | 'fields'>,
  extra: Record<string, string> = {},
  sender?: Record<string, string> | null,
): Record<string, string> {
  const name = lead.name ?? ''
  const fields = lead.fields ?? {}

  if (sender) {
    for (const key of Object.keys(fields)) {
      if (!(key in sender) || warnedCollisions.has(key)) continue
      warnedCollisions.add(key)
      console.warn(
        `[merge] lead column "${key}" is ignored for {{${key}}} — that token is a sender/campaign constant, not per-recipient`,
      )
    }
  }

  return {
    name,
    email: lead.email,
    first_name: name.split(' ')[0] ?? '',
    ...fields,
    ...(sender ?? {}),
    ...extra,
  }
}

export interface TokenIssue {
  token: string
  kind: TokenKind
  /** plain-words instruction for whoever has to fix it */
  fix: string
}

/**
 * Every token in the template that would render EMPTY.
 *
 * Split by class so the two callers can act differently: the wizard blocks on
 * `sender` issues before a campaign can launch (they are wrong for the whole
 * send), while the send path fails one lead on `lead`/`custom` issues and lets
 * the rest of the batch go.
 *
 * Pass no `lead` to check only the send-wide constants.
 */
export function unresolvedTokens(
  template: Pick<Template, 'subject' | 'body_text' | 'body_html'>,
  ctx: {
    sender?: Record<string, string> | null
    lead?: Pick<Lead, 'name' | 'email' | 'fields'> | null
  },
): TokenIssue[] {
  const tokens = extractTokens(template.subject, template.body_text, template.body_html)
  const vars = ctx.lead
    ? mergeVars(ctx.lead, { unsubscribe_url: 'x' }, ctx.sender)
    : { ...(ctx.sender ?? {}), unsubscribe_url: 'x' }

  const issues: TokenIssue[] = []
  for (const token of tokens) {
    const kind = classifyToken(token)
    // Without a lead in hand, per-recipient tokens are not yet knowable.
    if (!ctx.lead && kind !== 'sender') continue
    if (kind === 'sender' && !ctx.sender) continue
    if ((vars[token] ?? '').trim()) continue

    issues.push({
      token,
      kind,
      fix:
        kind === 'sender'
          ? (SENDER_TOKEN_SOURCE[token] ?? 'set this on the campaign or identity')
          : kind === 'lead'
            ? `this lead has no ${token}`
            : `no "${token}" column on this lead — add it to the CSV or remove the token`,
    })
  }
  return issues
}

/**
 * Replaces `{{key}}` with its value. Unknown keys collapse to an empty string
 * rather than leaking `{{company}}` into a real recipient's inbox.
 */
export function renderString(tpl: string, vars: Record<string, string>): string {
  return (tpl ?? '').replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k: string) => vars[k] ?? '')
}

/**
 * Merge values are DATA, not markup.
 *
 * A lead's name or a CSV column can contain anything, so every substituted
 * value is escaped before it enters HTML. Without this a lead called
 * `<script>…` writes tags straight into the outgoing email.
 */
export function escapeVars(vars: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(vars)) out[k] = escapeHtml(String(v ?? ''))
  return out
}

/**
 * A header holds ONE line. CRLF in a merged value would let a lead field append
 * its own headers (Bcc:, Reply-To:) to the message — so line breaks collapse to
 * a space before anything reaches a header.
 */
export function headerSafe(s: string): string {
  return (s ?? '').replace(/[\r\n]+/g, ' ').trim()
}

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/** Plain text -> simple paragraph HTML, used when a template has no body_html. */
export function textToHtml(text: string): string {
  return text
    .split(/\n{2,}/)
    .map((p) => `<p>${escapeHtml(p).replace(/\n/g, '<br />')}</p>`)
    .join('\n')
}

/**
 * Rich HTML -> readable plain text: block tags become newlines, list items get
 * a dash, link TEXT is kept (not the URL), everything else is stripped and
 * entities decoded. Used to derive body_text from the editor's body_html so the
 * text/plain part of every email stays honest.
 */
export function htmlToText(html: string): string {
  let s = html ?? ''
  s = s.replace(/<style[\s\S]*?<\/style>/gi, '').replace(/<script[\s\S]*?<\/script>/gi, '')
  s = s.replace(/<br\s*\/?>/gi, '\n')
  s = s.replace(/<li[^>]*>/gi, '- ')
  s = s.replace(/<\/(p|div|h[1-6]|li|blockquote|tr)>/gi, '\n')
  s = s.replace(/<\/(ul|ol)>/gi, '\n')
  s = s.replace(/<[^>]+>/g, '')
  s = s
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;/g, "'")
    .replace(/&amp;/g, '&')
  // tidy: no trailing space runs, at most one blank line in a row
  s = s
    .split('\n')
    .map((line) => line.replace(/\s+$/g, ''))
    .join('\n')
  s = s.replace(/\n{3,}/g, '\n\n')
  return s.trim()
}

const UNSUB_TOKEN = /\{\{\s*unsubscribe_url\s*\}\}/

/**
 * Tokens every lead already has (or that the sender fills in), so they are
 * never "columns the CSV must supply".
 *
 * The sender constants belong here too: {{company}} is answered by the campaign,
 * so demanding a `company` column on import was the original mistake.
 */
export const BUILTIN_TOKENS = [
  ...LEAD_TOKENS,
  ...SYSTEM_TOKENS,
  ...SENDER_TOKENS,
] as readonly string[] as string[]

/** Every distinct {{token}} across the given strings, in first-seen order. */
export function extractTokens(...parts: (string | null | undefined)[]): string[] {
  const found: string[] = []
  for (const part of parts) {
    for (const m of (part ?? '').matchAll(/\{\{\s*(\w+)\s*\}\}/g)) {
      if (!found.includes(m[1])) found.push(m[1])
    }
  }
  return found
}

/**
 * The merge fields a template expects a lead to supply — i.e. the columns an
 * import needs beyond email/name. Drives both the Leads table columns and the
 * import dialog's guidance, so the two can never disagree.
 */
export function customMergeFields(
  template: Pick<Template, 'subject' | 'body_text' | 'body_html'> | null | undefined,
): string[] {
  if (!template) return []
  return extractTokens(template.subject, template.body_text, template.body_html).filter(
    (t) => !BUILTIN_TOKENS.includes(t),
  )
}

export interface MergeFieldGroup {
  label: string
  tokens: string[]
}

/**
 * The "Insert field" menu, split by where a value actually comes from.
 *
 * The two groups are the whole mental model: the top group is the same in every
 * email of the send, the bottom group changes per recipient. Sender tokens are
 * always offered — they need no CSV column to exist.
 */
export function groupMergeFields(available: string[] = []): MergeFieldGroup[] {
  const fromLead: string[] = [...LEAD_TOKENS]
  for (const token of available) {
    const kind = classifyToken(token)
    if (kind === 'sender' || kind === 'system') continue
    if (!fromLead.includes(token)) fromLead.push(token)
  }
  return [
    { label: 'From your identity', tokens: [...SENDER_TOKENS] },
    { label: 'From the lead', tokens: fromLead },
  ]
}

export interface RenderedEmail {
  subject: string
  text: string
  html: string
}

/**
 * Renders one email and guarantees an unsubscribe link is present.
 *
 * If the template contains `{{unsubscribe_url}}` the link goes exactly where
 * the author put it; otherwise a footer is appended. There is no code path
 * that produces an email without a working opt-out.
 */
export function renderEmail(
  template: Pick<Template, 'subject' | 'body_text' | 'body_html'> &
    Partial<Pick<Template, 'body_mode'>>,
  lead: Pick<Lead, 'name' | 'email' | 'fields'>,
  unsubscribeUrl: string,
  /**
   * The send-wide constants. Optional so a bare preview still renders, but every
   * real path (sender, queue preview, test send, editor preview) passes it —
   * otherwise {{company}} and {{sender_name}} render empty.
   */
  sender?: Record<string, string> | null,
): RenderedEmail {
  const vars = mergeVars(lead, { unsubscribe_url: unsubscribeUrl }, sender)

  // Subject is a header: one line, no injected fields.
  const subject = headerSafe(renderString(template.subject, vars))
  // Text part takes values raw; the HTML part takes them escaped.
  const bodyText = renderString(template.body_text ?? '', vars)
  const bodyHtml = renderString(template.body_html ?? '', escapeVars(vars))

  const authorPlacedLink =
    UNSUB_TOKEN.test(template.body_text ?? '') || UNSUB_TOKEN.test(template.body_html ?? '')

  const text = authorPlacedLink
    ? bodyText
    : `${bodyText}\n\n—\nDon't want these? Unsubscribe: ${unsubscribeUrl}`

  const htmlBody = bodyHtml.trim() ? bodyHtml : textToHtml(bodyText)

  const footer = authorPlacedLink ? '' : unsubscribeFooter(unsubscribeUrl)

  // Who owns the layout?
  //   full document      -> the author does; use it as-is
  //   body_mode 'html'   -> the author does; give it a bare shell so a 600px
  //                         table is not squeezed by our own max-width
  //   rich / legacy text -> we do; apply the readable 560px wrapper
  const html = isFullDocument(htmlBody)
    ? injectBeforeBodyEnd(htmlBody, footer)
    : template.body_mode === 'html'
      ? bareShell(`${htmlBody}${footer}`)
      : wrapHtml(`${htmlBody}${footer}`)

  return { subject, text, html }
}

/** True when the author supplied a whole document rather than a fragment. */
export function isFullDocument(html: string): boolean {
  return /<html[\s>]/i.test(html) || /<body[\s>]/i.test(html) || /^\s*<!doctype/i.test(html)
}

function unsubscribeFooter(unsubscribeUrl: string): string {
  return `
<hr style="border:none;border-top:1px solid #e4e4e7;margin:24px 0 12px" />
<p style="font-size:12px;color:#71717a;margin:0;font-family:-apple-system,'Segoe UI',sans-serif;text-align:center">
  Don't want these? <a href="${escapeHtml(unsubscribeUrl)}" style="color:#5151cd">Unsubscribe</a>.
</p>`
}

/**
 * Appends markup just inside </body>, falling back to the end of the string
 * when the document has no closing body tag.
 */
export function injectBeforeBodyEnd(html: string, markup: string): string {
  if (!markup) return html
  const idx = html.toLowerCase().lastIndexOf('</body>')
  if (idx === -1) return `${html}${markup}`
  return `${html.slice(0, idx)}${markup}\n${html.slice(idx)}`
}

// --- tracking-aware rendering (real sends only) ------------------------------

export interface TrackingOptions {
  /** e.g. https://leadmailer.pages.dev — where /t/o and /t/c live */
  trackBase: string
  /** signs a tracking payload; the caller closes over campaign/lead/queue ids */
  sign: (extra: { u?: string }) => Promise<string>
}

/**
 * renderEmail plus tracking, used ONLY by the Worker for real deliveries:
 *   - every http(s) link in the HTML part is rewritten through /t/c with a
 *     signed token carrying the true target (so the redirect is unforgeable),
 *   - a /t/o open pixel is appended to the HTML,
 *   - the unsubscribe link is left alone,
 *   - the plain-text part is untouched.
 * Previews and dry runs keep calling renderEmail, so they never log events.
 */
export async function renderTrackedEmail(
  template: Pick<Template, 'subject' | 'body_text' | 'body_html'>,
  lead: Pick<Lead, 'name' | 'email' | 'fields'>,
  unsubscribeUrl: string,
  tracking: TrackingOptions,
  sender?: Record<string, string> | null,
): Promise<RenderedEmail> {
  const base = renderEmail(template, lead, unsubscribeUrl, sender)
  const trackBase = tracking.trackBase.replace(/\/+$/, '')
  let html = base.html

  // Collect http(s) hrefs; hrefs are HTML-escaped in the document, so compare
  // against the unescaped value and rewrite the raw attribute text.
  const seen = new Map<string, string>() // raw attr value -> tracked url
  for (const match of html.matchAll(/href="([^"]+)"/g)) {
    const raw = match[1]
    if (seen.has(raw)) continue
    const target = raw.replace(/&amp;/g, '&')
    if (!/^https?:\/\//i.test(target)) continue
    if (target === unsubscribeUrl) continue // opt-out must stay direct
    const token = await tracking.sign({ u: target })
    seen.set(raw, `${trackBase}/t/c?t=${encodeURIComponent(token)}`)
  }
  for (const [raw, tracked] of seen) {
    html = html.split(`href="${raw}"`).join(`href="${escapeHtml(tracked)}"`)
  }

  const openToken = await tracking.sign({})
  const pixel = `<img src="${escapeHtml(
    `${trackBase}/t/o?t=${encodeURIComponent(openToken)}`,
  )}" width="1" height="1" alt="" style="display:block;width:1px;height:1px;border:0" />`
  // Robust against pasted documents that omit </body>.
  html = injectBeforeBodyEnd(html, pixel)

  return { subject: base.subject, text: base.text, html }
}

/**
 * A valid document that imposes NO layout of its own — for html-mode templates
 * where the author's markup already carries the widths, fonts, and colours.
 */
function bareShell(inner: string): string {
  return `<!doctype html>
<html>
  <body style="margin:0;padding:0">
${inner}
  </body>
</html>`
}

/** Minimal email-safe shell. Inline styles only — no external CSS in email. */
function wrapHtml(inner: string): string {
  return `<!doctype html>
<html>
  <body style="margin:0;padding:0;background:#fafafa">
    <div style="max-width:560px;margin:0 auto;padding:24px;font-family:-apple-system,'Segoe UI',sans-serif;font-size:15px;line-height:1.6;color:#18181b">
${inner}
    </div>
  </body>
</html>`
}
